import { TAuthenticatedUser } from "@byte-coach/hutbot-mapp-sdk-js";
import NetworkManager from "./NetworkManager";
import { ECheckListProductLinkType } from "../constants/product";
import { TNotificationConfigItem, TSubmitTrackAndTraceNotificationConfigPayload, TSubmitTrackAndTraceNotificationConfigReponse } from "./type";


export interface SessionUser {
  firstName: string;
  lastName: string;
  roles: string[];
}

export interface ProductImage {
  id: string;
  brandId: string;
}

export interface Product {
  id: string;
  calculationReady: string;
  calculationDiscard: string;
  category: string;
  heading: string;
  subHeading: string;
  image?: ProductImage;

  category_1?: string;
  heading_1?: string;
  subHeading_1?: string;

  searchField?: string;

  // Properties for grouping structure
  __isCategory?: boolean;
  __categoryName?: string;
  __categoryExpanded?: boolean;
  __hidden?: boolean;
}

export interface Brand {
  id: string;
  image?: { id: string; brandId: string };
}

export type Country = {
  id: string;
  brandId: string;
  primaryLanguage: string;
  secondaryLanguage?: string;
  products?: Product[];
  temperatureUnit?: "C" | "F";
  features?: {
    haccpTrackAndTrace?: boolean;
    haccpNotificationRules?: {
      notificationBeforeDiscard?: { enabled: boolean; minutes: number };
      notificationAtDiscard?: { enabled: boolean };
      notificationAfterDiscard?: { enabled: boolean; minutes: number };
      notificationEveryXAfterDiscard?: { enabled: boolean; minutes: number };
    };
    // Add more features here as needed
  };
};

export type TCheckListProductLinkTypePayload = {
  name: ECheckListProductLinkType;
  commentSupported: boolean;
  commentRequired: boolean;
  imageSupported: boolean;
  imageRequired: boolean;
}

export type TChecklistTrackAndTraceItem = {
  nameResourceKey: string;
  name: string;
  type: TCheckListProductLinkTypePayload;
  required: boolean;
  checklistId: number;
}

export type TChecklistTrackAndTraceItemWithId = TChecklistTrackAndTraceItem & {
  id: number;
}

export type TChecklistTrackAndTraceSubmitItem = {
  productId: string;
  productName: string;
  category: string;
  nameResourceKey: string;
  required: boolean;
}

export type TGetLinkChecklistToQuestionItem = Pick<TChecklistTrackAndTraceItemWithId, 'nameResourceKey' | 'type' | 'id'>


export type TGetLinkChecklistToProductItem = {
  productId: string;
  productName: string;
  category?: string;
  questions: TGetLinkChecklistToQuestionItem[]
}

export type TGetLinkChecklistToProductResponse = TGetLinkChecklistToProductItem[]

type TLinkChecklistToProductPayload = {
  brand: string;
  countryIsoCode: string;
  products: TChecklistTrackAndTraceSubmitItem[]
}

export type TChecklistTrackAndTraceResponseItem = TChecklistTrackAndTraceSubmitItem & {
  id: number;
}

export type TLinkChecklistToProductResponse = {
  brandId: string;
  countryIsoCode: string;
  products: TChecklistTrackAndTraceResponseItem[]
}

export type TUnlinkChecklistToProductPayload = {
  id: number;
}[]

export type Image = {
  id: string;
  brandId: string;
  name: string;
  url: string
};

export type ImageDataUpload = {
  id: string;
  brandId: string;
  name: string;
  data: string;
};

export type Locale = { title: string; code: string };

export type User = TAuthenticatedUser;

export const API_BASE_URL = "/mapp-proxy/v1/mrds";
const X_BRAND_ID_HEADER = "x-brand-id";

export function putBrand(data: Partial<Brand>): Promise<Brand[]> {
  return NetworkManager.post(`${API_BASE_URL}/brands`, data, {
    "Content-Type": "application/json",
  });
}

export function getBrands(): Promise<Brand[]> {
  return NetworkManager.get(`${API_BASE_URL}/brands`);
}

export function getBrand(brandId: string): Promise<Brand> {
  return NetworkManager.get(`${API_BASE_URL}/brands/${brandId}`, undefined, {
    [X_BRAND_ID_HEADER]: brandId,
  });
}

export function updateBrand(data: Partial<Brand>): Promise<Brand[]> {
  return NetworkManager.put(`${API_BASE_URL}/brands/${data.id}`, data, data.id ? {
    [X_BRAND_ID_HEADER]: data.id} : undefined);
}

export function deleteBrand(id: string): Promise<Brand[]> {
  return NetworkManager.delete(`${API_BASE_URL}/brands/${id}`, undefined, {
    [X_BRAND_ID_HEADER]: id,
  });
}

export function putCountry(data: Partial<Country>): Promise<Country[]> {
  return NetworkManager.post(`${API_BASE_URL}/countries`, data);
}

export function getCountries(): Promise<Country[]> {
  return NetworkManager.get(`${API_BASE_URL}/countries`);
}

export function getTrackAndTraceChecklists({
  brandId,
  countryIsoCode,
  category = 'TRACK_AND_TRACE'
}: {
  brandId: string;
  countryIsoCode: string;
  category?: string;
}): Promise<TChecklistTrackAndTraceItem[]> {
  return NetworkManager.get(`/mapp-proxy/v1/checklists/items?brand=${brandId}&countryIsoCode=${countryIsoCode}&category=${category}`, undefined, {
    [X_BRAND_ID_HEADER]: brandId,
  });
}


export function getLinkChecklistToProduct({
  brandId,
  countryIsoCode,
}: {
  brandId: string;
  countryIsoCode: string;
}): Promise<TGetLinkChecklistToProductResponse> {
  return NetworkManager.get<TGetLinkChecklistToProductResponse>(`/mapp-proxy/v1/checklists/items/products?brand=${brandId}&countryIsoCode=${countryIsoCode}`, undefined, {
    [X_BRAND_ID_HEADER]: brandId,
  });
}


export function linkChecklistToProduct(data: TLinkChecklistToProductPayload): Promise<TLinkChecklistToProductResponse> {
  return NetworkManager.post<TLinkChecklistToProductResponse>(`/mapp-proxy/v1/checklists/items/products`, data, {
    "Content-Type": "application/json",
    [X_BRAND_ID_HEADER]: data.brand,
  });
}

export function unlinkChecklistToProduct(
  productId: string,
  data: TUnlinkChecklistToProductPayload,
  brandId: string
): Promise<void> {
  return NetworkManager.post<void>(
    `/mapp-proxy/v1/checklists/items/products/${productId}/batch-removal`,
    data,
    {
      "Content-Type": "application/json",
      [X_BRAND_ID_HEADER]: brandId,
    }
  );
}

export function getNotificationConfigByBrandAndCountry({
  brand,
  countryIsoCode,
}: {
  brand: string;
  countryIsoCode: string;
}): Promise<TNotificationConfigItem> {
  return NetworkManager.get<TNotificationConfigItem>(`/mapp-proxy/v1/mrds/notification-configs/${brand}/${countryIsoCode}`, undefined, {
    [X_BRAND_ID_HEADER]: brand,
  });
}


export function submitTrackAndTraceNotificationConfig(data: TSubmitTrackAndTraceNotificationConfigPayload): Promise<TSubmitTrackAndTraceNotificationConfigReponse> {
  return NetworkManager.post<TSubmitTrackAndTraceNotificationConfigReponse>(`/mapp-proxy/v1/mrds/notification-configs`, data, {
    "Content-Type": "application/json",
    [X_BRAND_ID_HEADER]: data.brand,
  });
}

export function getCountry({
  id,
  brandId,
}: {
  id: string;
  brandId: string;
}): Promise<Country> {
  return NetworkManager.get(
    `${API_BASE_URL}/countries/${id}?brandId=${brandId}`, undefined, {
      [X_BRAND_ID_HEADER]: brandId,
    }
  );
}

export function updateCountry(data: Partial<Country>): Promise<Country[]> {
  return NetworkManager.put(
    `${API_BASE_URL}/countries/${data.id}?brandId=${data.brandId}`,
    data,
    {
      "Content-Type": "application/json",
      ...(data.brandId ? { [X_BRAND_ID_HEADER]: data.brandId } : {}),
    }
  );
}

export function updateHaccpProduct(
  countryId: string,
  productId: string,
  brandId: string,
  data: any
): Promise<Country[]> {
  return NetworkManager.put(
    `${API_BASE_URL}/countries/${countryId}/products/${productId}/haccp-tracking?brandId=${brandId}`,
    data,
    {
      "Content-Type": "application/json",
      [X_BRAND_ID_HEADER]: brandId,
    }
  );
}

export function deleteCountry({
  id,
  brandId,
}: {
  id: string;
  brandId: string;
}): Promise<Country[]> {
  return NetworkManager.delete(
    `${API_BASE_URL}/countries/${id}?brandId=${brandId}`, undefined, {
      [X_BRAND_ID_HEADER]: brandId,
    }
  );
}

export function putImage(data: Partial<Image>): Promise<Image> {
  return NetworkManager.post(`${API_BASE_URL}/images`, data);
}

export function getImages({ brandId }: { brandId?: string }): Promise<Image[]> {
  return NetworkManager.get(`${API_BASE_URL}/images?brandId=${brandId}`, undefined, brandId ? {
    [X_BRAND_ID_HEADER]: brandId,
  } : undefined);
}

export function getImageById({ id, brandId }: { id: string, brandId: string }): Promise<Image> {
  return NetworkManager.get(`${API_BASE_URL}/images/${id}?brandId=${brandId}`, undefined, brandId ? {
    [X_BRAND_ID_HEADER]: brandId,
  } : undefined);
}

export function deleteImage({
  id,
  brandId,
}: {
  id: string;
  brandId: string;
}): Promise<Image[]> {
  return NetworkManager.delete(
    `${API_BASE_URL}/images/${id}?brandId=${brandId}`, undefined, brandId ? {
      [X_BRAND_ID_HEADER]: brandId,
    } : undefined
  );
}

export function getPermissions(): Promise<{ status: boolean; data: User }> {
  return NetworkManager.get(`${API_BASE_URL}/auth/permissions`);
}

export interface LabelPrintData {
  brand: string;
  country: string;
  storeNo?: string;
  champsNo: string;
  products: any[];
  isReprint?: boolean;
}

export async function logLabelPrint(data: LabelPrintData): Promise<void> {
  try {
    await NetworkManager.post(`${API_BASE_URL}/labels/print`, data, {
      "Content-Type": "application/json",
    });
  } catch (error) {
    // Silently fail to not interrupt printing process
    console.warn('Failed to log label print event:', error);
  }
}

export async function getLocales(): Promise<Locale[]> {
  return [
    { title: "None", code: "" },
    { title: "English", code: "en" },
    { title: "Spanish", code: "es" },
    { title: "French", code: "fr" },
    { title: "German", code: "de" },
    { title: "Hebrew", code: "he" },
    { title: "Chineese", code: "zh" },
    { title: "Indo", code: "id" },
    { title: "Japanese", code: "ja" },
    { title: "Korean", code: "ko" },
    { title: "Malai", code: "ms" },
    { title: "Mongolian", code: "mn" },
    { title: "Arabic", code: "ar" },
    { title: "Polish", code: "pl" },
    { title: "Portuguese", code: "pt" },
    { title: "Romanian", code: "ro" },
    { title: "Russian", code: "ru" },
    { title: "Swedish", code: "sv" },
    { title: "Thai", code: "th" },
    { title: "Turk", code: "tr" },
    { title: "Vietnamese", code: "vi" },
    { title: "Georgian", code: "ka" },
    { title: "Azerbaijani", code: "az" },
    { title: "Uzbek", code: "uz" },
    { title: "Kyrgyz", code: "ky" },
    { title: "Armenian", code: "hy" },
    { title: "Kazakh", code: "kk" },
    { title: "Tajik", code: "tg" },
  ];
}
